import React, { useEffect, useRef, useState } from "react";
import {
  REIButton,
  REIDataTable,
  REIDropDown,
  REIInputNumber,
  REIInputText,
  REIToastContainer,
} from "../../../../components/ui";
import { COUNTRY_OPTIONS, ENTITY_TYPE, MEMBER_KEY, Messages, USER_DETAIL } from "../../../../utils/constants";
import { httpPost, useHttpGets } from "../../../../services/httpService";
import { IMemberEntity, IMemberProfile } from "../../../../interface/analysed";
import { ApiEndPoints } from "../../../../utils/endpoints";
import { Toast } from "primereact/toast";
import { fetchMemberEntities } from "../../../../store/slice/myTrackerSlice";
import { AppDispatch } from "../../../../store";
import { useDispatch } from "react-redux";

interface EntityOwnerShipProps {
  closeDialogue: (data?: any) => void;
}

export const EntityOwnerShip: React.FC<EntityOwnerShipProps> = ({ closeDialogue }) => {
  const toastRef = useRef<Toast>(null);
  const [shoAddNewProfile, setShowAddNewProfile] = useState<boolean>(false);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [memberEntities, setMemberEntities] = useState<IMemberEntity[]>([]);
  const [memberProfiles, setMemberProfiles] = useState<IMemberProfile[]>([]);
  const [memberProfile, setMemberProfile] = useState<number>();
  const [selectedProfiles, setSelectedProfiles] = useState<any[]>([]);

  const [seelctedCountry, setSelectedCountry] = useState<number>(1);
  const [entityType, setEntityType] = useState<string>("PER");
  const [entityName, setEntityName] = useState<string>("");
  const [entityDescription, setEntityDescription] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const dispatch: AppDispatch = useDispatch();

  const [portfolio, setPortfolio] = useState<any>({
    PortfolioValue: 0,
    Equity: 0,
    AnnualRent: 0,
    PreTaxCashFlow: 0,
    AfterTaxCashFlow: 0,
  });

  const [profile, setProfile] = useState<any>({
    ProfileName: "",
    Income: 0,
    Owns: 0,
    MemberProfileId: 0,
  });

  const [showDuplicateMessage, setShowDuplicateMessage] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState("");
  const [duplicateProfile, setDuplicateProfile] = useState<any>(null);

  useEffect(() => {
    if (memberDetail) {
      useHttpGets(
        `${ApiEndPoints.tracker}/MemberEntities?memberId=${localStorage.getItem(MEMBER_KEY)}&userCountryCode=${
          memberDetail.Country
        }&memberName=${memberDetail.FirstName + " " + memberDetail.LastName}`,
        undefined
      )
        .then((result) => {
          if (result?.data != null && !result.error) {
            if (Array.isArray(result.data)) {
              setMemberEntities((result.data as any).filter((x: any) => x.EntityName !== "Personal Ownership"));
            }
          }
        })
        .catch((error) => {});
    }
    useHttpGets(`${ApiEndPoints.tracker}/GetMemberProfiles/${localStorage.getItem(MEMBER_KEY)}`, undefined)
      .then((result) => {
        if (result?.data != null && !result.error) {
          setMemberProfiles(result.data);
        }
      })
      .catch((error) => {});
  }, []);

  useEffect(() => {
    if (memberProfile) {
      const member = memberProfiles.filter((x: any) => x.MemberProfileId === memberProfile);
      setShowAddNewProfile(true);
      if (member && member.length > 0) {
        setProfile({
          ProfileName: member[0].MemberName,
          Income: member[0].Income,
          Owns: profile.Owns,
          MemberProfileId: memberProfile,
        });
      }
    }
  }, [memberProfile]);

  useEffect(() => {
    if (selectedProfiles.length) {
      const filteredProfiles = memberProfiles.filter(
        (x) => !selectedProfiles.some((y) => y.MemberProfileId === x.MemberProfileId)
      );
      setMemberProfiles(filteredProfiles);
    }
  }, [selectedProfiles]);

  const addNewProfile = () => {
    const errors: string[] = [];

    if (!profile.ProfileName.trim()) {
      errors.push("Profile name is required.");
    }

    if (profile.Income <= 0) {
      errors.push("Income must be greater than 0.");
    }

    if (profile.Owns <= 0 || profile.Owns > 100) {
      errors.push("Owns (%) must be between 1 and 100.");
    }

    if (selectedProfiles.reduce((acc, x) => acc + x.Owns, 0) + profile.Owns > 100) {
      errors.push("Total entity ownership cannot exceed 100%.This profile can have a maximum of 100% ownership");
    }

    if (selectedProfiles.some((x) => x.ProfileName === profile.ProfileName)) {
      const existingProfile = selectedProfiles.find((x) => x.ProfileName === profile.ProfileName);
      const message = `You already have a profile called ${profile.ProfileName} attached to this entity, with an income of ${existingProfile.Income}. Would you like to use this record instead or add a new record?`;

      setDuplicateMessage(message);
      setDuplicateProfile(profile);
      setShowDuplicateMessage(true);
      return;
    }

    if (errors.length > 0) {
      errors.forEach((error) => {
        toastRef.current?.show({
          severity: "error",
          summary: Messages.ERROR,
          detail: error,
          life: 2000,
        });
      });
      return;
    }
    setShowAddNewProfile(false);
    setSelectedProfiles([...selectedProfiles, profile]);
    setProfile({
      ProfileName: "",
      Income: 0,
      Owns: 0,
      MemberProfileId: 0,
    });
  };

  const handleEdit = (row: any) => {
    const updatedProfiles = selectedProfiles.map((profile) => {
      if (profile.MemberProfileId === row.MemberProfileId) {
        return { ...profile, isEditing: true };
      }
      return profile;
    });
    setSelectedProfiles(updatedProfiles);
  };

  const handleSaveEdit = (row: any) => {
    const totalOwns = selectedProfiles.reduce((acc, x) => acc + (x.isEditing ? 0 : x.Owns), 0) + row.Owns;
    if (totalOwns > 100) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: "Total entity ownership cannot exceed 100%.",
        life: 2000,
      });
      return;
    }
    const updatedProfiles = selectedProfiles.map((profile) => {
      if (profile.MemberProfileId === row.MemberProfileId) {
        return { ...profile, isEditing: false };
      }
      return profile;
    });
    setSelectedProfiles(updatedProfiles);
  };

  const handleDelete = (row: any) => {
    if (row && row.MemberProfileId !== undefined) {
      setSelectedProfiles(selectedProfiles.filter((x) => x.ProfileName !== row.ProfileName));
      if (row.MemberProfileId > 0) {
        setMemberProfiles([
          ...memberProfiles,
          {
            Income: row.Income,
            MemberName: row.ProfileName,
            MemberProfileId: row.MemberProfileId,
          },
        ]);
      }
    } else {
      console.error("Invalid row data provided to handleDelete:", row);
    }
  };

  const handleSaveClick = async () => {
    if (entityName.trim() === "") {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: "Entity name is required.",
        life: 2000,
      });
      return;
    }

    if (selectedProfiles.length === 0) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: "At least one profile must be selected.",
        life: 2000,
      });
      return;
    }

    if (memberEntities.some((x) => x.EntityName == entityName)) {
      toastRef.current?.show({
        severity: "error",
        summary: Messages.ERROR,
        detail: `You already have an entity called ${entityName}, please create a unique name for this entity.`,
        life: 4000,
      });
      return;
    }

    const body = {
      CountryCode: seelctedCountry === 1 ? "AU" : "NZ",
      EntityTypeCode: entityType,
      GoalPortfolioValue: portfolio.PortfolioValue,
      GoalEquityUnlocked: portfolio.Equity,
      GoalAnnualRent: portfolio.AnnualRent,
      GoalPreTaxCashFlow: portfolio.PreTaxCashFlow,
      GoalAfterTaxCashFlow: portfolio.AfterTaxCashFlow,
      IsDefault: memberEntities.length > 0 && memberEntities[0].ValuesDefaulted ? 1 : 0,
      MemberId: localStorage.getItem(MEMBER_KEY),
      EntityName: entityName,
      GoalDesc: entityDescription,
      Note: notes,
      MemberProfiles: selectedProfiles.map((x) => {
        return {
          MemberName: x.ProfileName,
          Income: x.Income,
          PortionOwned: x.Owns,
          MemberProfileId: x.MemberProfileId,
        };
      }),
    };

    await httpPost(`${ApiEndPoints.tracker}MemberEntity/Add/`, body)
      .then((result) => {
        if (result.status == 200) {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: "Entity added successfully",
            life: 2000,
          });
          closeDialogue(result);
          dispatch(fetchMemberEntities());
        }
      })
      .catch(() => {
        toastRef.current?.show({
          severity: "success",
          summary: Messages.ERROR,
          detail: Messages.ERROR,
          life: 2000,
        });
      });
  };

  const columns = [
    {
      field: "ProfileName",
      header: "Name",
      height: "39px",
      width: "200px",
    },
    {
      field: "Income",
      header: "Income",
      width: "100px",
      bodyTemplate: (row: any) =>
        row.isEditing ? (
          <REIInputNumber
            id={`aeo-num-income-${row.MemberProfileId}`}
            value={row.Income}
            onValueChange={(e) => {
              const updatedProfiles = selectedProfiles.map((profile) => {
                if (profile.MemberProfileId === row.MemberProfileId) {
                  return { ...profile, Income: e.target.value };
                }
                return profile;
              });
              setSelectedProfiles(updatedProfiles);
            }}
          />
        ) : (
          <span>{row.Income}</span>
        ),
    },
    {
      field: "Owns",
      header: "Owns(%)",
      width: "100px",
      bodyTemplate: (row: any) =>
        row.isEditing ? (
          <REIInputNumber
            id={`aeo-num-owns-${row.MemberProfileId}`}
            value={row.Owns}
            max={100}
            maxFractionDigits={2}
            onValueChange={(e) => {
              const updatedProfiles = selectedProfiles.map((profile) => {
                if (profile.MemberProfileId === row.MemberProfileId) {
                  return { ...profile, Owns: e.target.value };
                }
                return profile;
              });
              setSelectedProfiles(updatedProfiles);
            }}
          />
        ) : (
          <span>{row.Owns}</span>
        ),
    },
    {
      field: "MemberProfileId",
      header: "",
      type: "custom",
      bodyTemplate: (row: any) =>
        row.isEditing ? (
          <span className="text-blue-700 cursor-pointer" onClick={() => handleSaveEdit(row)}>
            Save
          </span>
        ) : (
          <span className="text-blue-700 cursor-pointer" onClick={() => handleEdit(row)}>
            Edit
          </span>
        ),
      width: "50px",
    },
    {
      field: "Id",
      header: "",
      type: "custom",
      bodyTemplate: (row: any) => (
        <i
          id={`aeo-icon-delete-${row.MemberProfileId}`}
          data-testid={`aeo-icon-delete-${row.MemberProfileId}`}
          className="pi pi-minus-circle text-blue-700 cursor-pointer"
          style={{ fontSize: "1rem" }}
          onClick={() => handleDelete(row)}
          title="Remove profile"
        ></i>
      ),
      width: "50px",
    },
  ];

  return (
    <div className="">
      <REIToastContainer toastReference={toastRef}></REIToastContainer>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-1/3">
          <label>Entity country</label>
          <REIDropDown
            id="aeo-drop-country"
            data={COUNTRY_OPTIONS}
            value={seelctedCountry}
            onSelectionChange={(e) => {
              setSelectedCountry(e.value);
            }}
            label="name"
            optionValue="value"
            placeholder="Select entity country"
          />
        </div>
        <div className="flex-0 w-1/3">
          <label>Entity Type</label>
          <REIDropDown
            id="aeo-drop-entitytype"
            data={ENTITY_TYPE}
            value={entityType}
            onSelectionChange={(e) => {
              setEntityType(e.value);
            }}
            label="name"
            optionValue="value"
            placeholder="Select Entity type"
          />
        </div>
        <div className="flex-0 w-1/3"></div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full">
          <label>Entity Name*</label>
          <REIInputText
            id="aeo-txt-entityname"
            className={`w-full ${entityName.trim() === "" ? "border-red-500" : ""}`}
            type="text"
            placeholder=""
            value={entityName}
            onChange={(e) => setEntityName(e.target.value)}
            disabled={false}
            name="entityName"
          />
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full">
          <label>User profiles for this entity</label>
          <br />
          <br />
          <label className="mt-2">
            This is important for calculating tax implications accurately across your portfolio. Please ensure all
            profiles have a unique name.
          </label>
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full overflow-y-auto">
          {showDuplicateMessage ? (
            <div className="flex flex-col items-center justify-center">
              <p>{duplicateMessage}</p>
              <div className="flex gap-2 mt-2">
                <REIButton
                  id="aeo-btn-userecord"
                  label="Use this record"
                  onClick={() => {
                    const updatedProfiles = selectedProfiles.map((x) =>
                      x.ProfileName === duplicateProfile.ProfileName ? { ...duplicateProfile } : x
                    );
                    setSelectedProfiles(updatedProfiles);
                    setShowDuplicateMessage(false);

                    setShowAddNewProfile(false);
                    setProfile({
                      ProfileName: "",
                      Income: 0,
                      Owns: 0,
                      MemberProfileId: 0,
                    });
                  }}
                />
                <REIButton
                  id="aeo-btn-newrecord"
                  label="Add a new record"
                  onClick={() => {
                    const newProfileName = `${duplicateProfile.ProfileName} (${
                      selectedProfiles.filter((x) => x.ProfileName.startsWith(duplicateProfile.ProfileName)).length + 1
                    })`;
                    setSelectedProfiles([...selectedProfiles, { ...duplicateProfile, ProfileName: newProfileName }]);
                    setShowDuplicateMessage(false);

                    setShowAddNewProfile(false);
                    setProfile({
                      ProfileName: "",
                      Income: 0,
                      Owns: 0,
                      MemberProfileId: 0,
                    });
                  }}
                />
              </div>
            </div>
          ) : (
            <REIDataTable
              key={selectedProfiles.map((p) => p.MemberProfileId).join("-")}
              data={selectedProfiles}
              columns={columns}
              actions={undefined}
              customclass="max-h-40"
            />
          )}
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        {memberProfiles && memberProfiles.length > 0 && (
          <div className="flex-0 w-1/3">
            <label>Select an existing profile*</label>
            <REIDropDown
              id="aeo-drop-existingprofile"
              data={memberProfiles}
              value={memberProfile}
              onSelectionChange={(e) => {
                setMemberProfile(e.value);
              }}
              label="MemberName"
              optionValue="MemberProfileId"
              placeholder="Select existing profile"
            />
          </div>
        )}
        <div className="flex-0 w-1/3 gap-1">
          <div className="flex items-center gap-1">
            {memberProfiles && memberProfiles.length > 0 && <span className="mt-2 mx-1">or</span>}
            <REIButton
              id="aeo-btn-addprofile"
              label="Add a new profile to this entity"
              icon={"pi pi-plus"}
              type="button"
              iconPos="right"
              disabled={shoAddNewProfile}
              classNames="p-button-primary mt-5"
              onClick={() => {
                setShowAddNewProfile(true);
              }}
            />
          </div>
        </div>
        <div className="flex-0 w-1/3"></div>
      </div>

      {shoAddNewProfile && (
        <div className="flex flex-row w-full p-1 gap-1 mt-3">
          <div className="flex-0 w-[40%]">
            <label>Name</label>
            <REIInputText
              id="aeo-txt-profilename"
              className={`w-full ${profile.ProfileName == "" ? "border-red-500" : ""}`}
              type="text"
              placeholder=""
              value={profile.ProfileName}
              disabled={profile.MemberProfileId > 0 ? true : false}
              onChange={(e) => {
                setProfile({ ...profile, ProfileName: e.target.value });
              }}
              name="profileName"
            />
          </div>
          <div className="flex-0 w-[20%]">
            <label>Income ($)</label>
            <REIInputNumber
              id="aeo-num-income"
              className="w-full"
              invalid={profile.Income <= 0}
              key={"income"}
              value={profile.Income}
              onValueChange={(e) => {
                setProfile({ ...profile, Income: e.target.value });
              }}
              min={1}
            />
          </div>
          <div className="flex-0 w-[20%]">
            <label>Owns (%)*</label>
            <REIInputNumber
              id="aeo-num-owns"
              className="w-full"
              invalid={profile.Owns <= 0 || profile.Owns > 100}
              key={"owns"}
              value={profile.Owns}
              onValueChange={(e) => {
                setProfile({ ...profile, Owns: e.target.value });
              }}
              min={0}
              max={100}
              maxFractionDigits={2}
            />
          </div>
          <div className="flex-0 w-[20%]">
            <div className="flex items-center gap-1 mt-5">
              <REIButton
                id="aeo-btn-add"
                label="Add"
                onClick={() => {
                  addNewProfile();
                }}
              />
              <REIButton
                id="aeo-btn-cancelprofile"
                label="Cancel"
                classNames=""
                onClick={() => {
                  setShowAddNewProfile(false);
                }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full">
          <label>Describe this entity's goals</label>
          <REIInputText
            id="aeo-txt-goals"
            className="w-full"
            type="text"
            placeholder=""
            value={entityDescription}
            onChange={(e) => {
              setEntityDescription(e.target.value);
            }}
            disabled={false}
            name="entityName"
          />
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full">
          <label>Goals for this entity</label>
        </div>
      </div>
      <div className="border border-solid border-gray-300 w-full gap-1 rounded-lg mt-3">
        <div className="flex flex-row w-full bg-slate-200 gap-1 min-h-8 items-center rounded-t-lg  border border-x-0 border-t-0 border-b border-solid border-gray-300">
          <div className="flex-0 w-1/6"></div>
          <div className="flex-0 w-1/6 font-bold">Portfolio value</div>
          <div className="flex-0 w-1/6 font-bold">Equity</div>
          <div className="flex-0 w-1/6 font-bold">Annual rent</div>
          <div className="flex-0 w-1/6 font-bold">Pre-tax cash flow pa</div>
          <div className="flex-0 w-1/6 font-bold">After-tax cash flow pa</div>
        </div>
        <div className="flex flex-row w-full gap-1 items-center py-1">
          <div className="flex-0 w-1/6 text-center">Target</div>
          <div className="flex-0 w-1/6">
            <REIInputNumber
              id="aeo-num-portfoliovalue"
              className="w-full"
              key={"portfolioValue"}
              value={portfolio.PortfolioValue}
              onValueChange={(e) => {
                setPortfolio({ ...portfolio, PortfolioValue: e.target.value });
              }}
            />
          </div>
          <div className="flex-0 w-1/6">
            <REIInputNumber
              id="aeo-num-equity"
              className="w-full"
              key={"equity"}
              value={portfolio.Equity}
              onValueChange={(e) => {
                setPortfolio({
                  ...portfolio,
                  Equity: e.target.value,
                });
              }}
            />
          </div>
          <div className="flex-0 w-1/6">
            <REIInputNumber
              id="aeo-num-annualrent"
              className="w-full"
              key={"annualRent"}
              value={portfolio.AnnualRent}
              onValueChange={(e) => {
                setPortfolio({
                  ...portfolio,
                  AnnualRent: e.target.value,
                });
              }}
            />
          </div>
          <div className="flex-0 w-1/6">
            <REIInputNumber
              id="aeo-num-pretax"
              className="w-full"
              key={"pretax"}
              value={portfolio.PreTaxCashFlow}
              onValueChange={(e) => {
                setPortfolio({
                  ...portfolio,
                  PreTaxCashFlow: e.target.value,
                });
              }}
            />
          </div>
          <div className="flex-0 w-1/6">
            <REIInputNumber
              id="aeo-num-aftertax"
              className="w-full pr-1"
              key={"aftertax"}
              value={portfolio.AfterTaxCashFlow}
              onValueChange={(e) => {
                setPortfolio({
                  ...portfolio,
                  AfterTaxCashFlow: e.target.value,
                });
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-row w-full p-1 gap-1 mt-3">
        <div className="flex-0 w-full">
          <label className="mt-2">
            To add properties to this entity, please select this entity name when adding a new property to Tracker, or
            when editing an existing property.
          </label>
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1">
        <div className="flex-0 w-full">
          <label>Notes</label>
          <REIInputText
            id="aeo-txt-notes"
            className="w-full"
            type="text"
            placeholder=""
            value={notes}
            disabled={false}
            onChange={(e) => {
              setNotes(e.target.value);
            }}
            name="entityName"
          />
        </div>
      </div>
      <div className="flex flex-row w-full p-1 gap-1">
        <div className="flex-0 w-full text-right">
          <REIButton id="aeo-btn-cancel" label="Cancel" classNames="mr-1 p-button-link" onClick={closeDialogue} />
          <REIButton id="aeo-btn-save" label="Save" onClick={handleSaveClick} />
        </div>
      </div>
    </div>
  );
};

export default EntityOwnerShip;
